import useCollection from 'hooks/useCollection'
import {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode
} from 'react'
import useUserWallets from 'hooks/useUserWallets'
import { RegisteredWallet } from 'types'
import ConnectWalletModal from 'components/Modal/ConnectWalletModal'

interface State {
  registeredWallets: RegisteredWallet[]
  flowUser: any
  hasMetaMask: boolean
  hasMagic: boolean
  hasCrossmint: boolean
  metamaskWalletAddress: string
  crossmintWalletAddress: string
  magicWalletAddress: string
  magicMomentCount: number
  metamaskMomentCount: number
  connectedWalletAddress: string
  isConnected: boolean
  connect?: any
  connectMetaMask: () => Promise<
    { success: boolean; error?: string } | undefined
  >
  connectCrossmint: () => Promise<
    { success: boolean; error?: string; walletAddress?: string } | undefined
  >
  connectMagic: () => Promise<{ success: boolean; error?: string } | undefined>
  disconnect?: any
  collectionIsLoading: boolean
  collection: boolean | undefined
  createCollection: () => void
  destroyCollection: () => void
  flowWUSDCBalance: number
  magicWalletWUSDCBalance: number
  metamaskWalletBalance: number
  getFlowWUSDCBalance: () => void
  usingMagic: boolean
  usingMetaMask: boolean
  usingCrossmint: boolean
  crossmintProvisioned: boolean
  userAuth: any
  magic: any
  completedLoadingWalletsInfo: boolean
  lastUsedWallet: string
  checkAndUpdateUsersRegisteredWallets: () => void
  refreshWallets: () => void
  magicForSaleMomentCount: number
  metamaskForSaleMomentCount: number
  magicNotForSaleMomentCount: number
  metamaskNotForSaleMomentCount: number
  crossmintMomentCount: number
  crossmintForSaleMomentCount: number
  crossmintNotForSaleMomentCount: number
  crossmintWalletBalance: number
  crossmintBalanceRefreshing: boolean
  refreshCrossmintBalance: () => Promise<void>
  refreshCrossmintBalanceWithPolling: (previousBalance: number) => Promise<void>
  showMagicWalletUI: () => void
  exportMagicWalletPK: () => void
  isExporting: boolean
  exportError: string | null
  // True when `addr` matches any wallet the current user owns (flow / crossmint /
  // metamask / magic), compared case-insensitively. Use to gate seller-only UI
  // like "you can't buy your own listing" — chain addresses come back lowercased
  // from the BE while in-app wallet state is often checksummed, so callers
  // shouldn't have to remember to normalize.
  isOwnAddress: (addr?: string) => boolean
  openWalletModal: () => void
}

interface WalletsContextProps {
  children: ReactNode
}

const walletsContextDefaultValues: State = {
  registeredWallets: [],
  flowUser: null,
  hasMetaMask: false,
  hasMagic: false,
  hasCrossmint: false,
  magicWalletAddress: '',
  metamaskWalletAddress: '',
  crossmintWalletAddress: '',
  connectedWalletAddress: '',
  magicMomentCount: 0,
  metamaskMomentCount: 0,
  connectMetaMask: async () => undefined,
  connectCrossmint: async () => undefined,
  connectMagic: async () => undefined,
  isConnected: false,
  connect: null,
  disconnect: null,
  collection: false,
  collectionIsLoading: true,
  createCollection: () => null,
  destroyCollection: () => null,
  flowWUSDCBalance: 0,
  magicWalletWUSDCBalance: 0,
  metamaskWalletBalance: 0,
  getFlowWUSDCBalance: () => null,
  usingMagic: false,
  usingMetaMask: false,
  usingCrossmint: false,
  crossmintProvisioned: false,
  userAuth: null,
  magic: null,
  completedLoadingWalletsInfo: false,
  lastUsedWallet: 'magic',
  checkAndUpdateUsersRegisteredWallets: () => undefined,
  refreshWallets: () => undefined,
  magicForSaleMomentCount: 0,
  metamaskForSaleMomentCount: 0,
  magicNotForSaleMomentCount: 0,
  metamaskNotForSaleMomentCount: 0,
  crossmintMomentCount: 0,
  crossmintForSaleMomentCount: 0,
  crossmintNotForSaleMomentCount: 0,
  crossmintWalletBalance: 0,
  crossmintBalanceRefreshing: false,
  refreshCrossmintBalance: () => Promise.resolve(),
  refreshCrossmintBalanceWithPolling: () => Promise.resolve(),
  showMagicWalletUI: () => undefined,
  exportMagicWalletPK: () => undefined,
  isExporting: false,
  exportError: null,
  isOwnAddress: () => false,
  openWalletModal: () => undefined
}

const WalletsContext = createContext<State>(walletsContextDefaultValues)

const METAMASK_INSTALL_URL =
  'https://chromewebstore.google.com/detail/metamask/nkbihfbeogaeaoehlefnkodbefgpgknn'

export default function WalletsProvider({ children }: WalletsContextProps) {
  const userWallets = useUserWallets()

  const collectionProps = useCollection(userWallets.flowUser)
  const [walletModalOpen, setWalletModalOpen] = useState(false)
  const [connecting, setConnecting] = useState<'metamask' | 'crossmint' | null>(
    null
  )
  const [connectError, setConnectError] = useState<string | null>(null)

  const wrappedCreateCollection = useCallback(() => {
    if (userWallets.usingMagic) {
      collectionProps.createCollection()
    } else {
      setConnectError(null)
      setWalletModalOpen(true)
    }
  }, [userWallets.usingMagic, collectionProps.createCollection])

  const closeWalletModal = useCallback(() => {
    setWalletModalOpen(false)
    setConnecting(null)
    setConnectError(null)
  }, [])

  const handleSelectMetaMask = useCallback(async () => {
    setConnectError(null)
    setConnecting('metamask')
    try {
      const result = await userWallets.connectMetaMask()
      if (result?.success) {
        closeWalletModal()
      } else if (result?.error === 'not_installed') {
        setConnectError('MetaMask is not installed. Opening the install page…')
        window.open(METAMASK_INSTALL_URL, '_blank')
      } else if (result?.error === 'no_accounts') {
        setConnectError(
          'No accounts found. Create an account in MetaMask first.'
        )
      } else {
        setConnectError(result?.error || 'Failed to connect MetaMask')
      }
    } catch (e: any) {
      setConnectError(e?.message || 'Failed to connect MetaMask')
    } finally {
      setConnecting(null)
    }
  }, [userWallets.connectMetaMask, closeWalletModal])

  // Match `addr` against every wallet flavor a user might have. EVM listings
  // come back from the BE lowercased, but in-app wallet state can be
  // checksummed — normalize both sides here so call-sites don't have to.
  // Also checks localStorage persisted addresses and registeredWallets (server-
  // fetched) as a fallback for timing windows where React state is not yet
  // hydrated (e.g. first render before useEffect runs).
  const isOwnAddress = useCallback(
    (addr?: string) => {
      if (!addr) return false
      const target = addr.toLowerCase()
      const candidates = [
        userWallets.flowUser?.addr,
        userWallets.crossmintWalletAddress,
        userWallets.metamaskWalletAddress,
        userWallets.magicWalletAddress,
        // localStorage fallbacks — populated by connectCrossmint / connectMetaMask
        // even when React state hasn't been hydrated yet on first render
        localStorage.getItem('crossmintWalletAddress'),
        localStorage.getItem('metamaskWalletAddress'),
        // All wallets registered to this user on the server (covers cases where
        // lastUsedWallet changed but an old address is still the seller)
        ...userWallets.registeredWallets.map((w: any) => w.walletAddress)
      ]
      return candidates.some((a) => !!a && a.toLowerCase() === target)
    },
    [
      userWallets.flowUser,
      userWallets.crossmintWalletAddress,
      userWallets.metamaskWalletAddress,
      userWallets.magicWalletAddress,
      userWallets.registeredWallets
    ]
  )

  const handleSelectCrossmint = useCallback(async () => {
    setConnectError(null)
    setConnecting('crossmint')
    try {
      const result = await userWallets.connectCrossmint()
      if (result?.success) {
        closeWalletModal()
      } else {
        setConnectError(result?.error || 'Failed to create Crossmint wallet')
      }
    } catch (e: any) {
      setConnectError(e?.message || 'Failed to create Crossmint wallet')
    } finally {
      setConnecting(null)
    }
  }, [userWallets.connectCrossmint, closeWalletModal])

  return (
    <WalletsContext.Provider
      value={{
        ...userWallets,
        ...collectionProps,
        createCollection: wrappedCreateCollection,
        connect: userWallets.tools?.connect,
        disconnect: userWallets.tools?.disconnect,
        isOwnAddress,
        openWalletModal: () => {
          setConnectError(null)
          setWalletModalOpen(true)
        }
      }}
    >
      {children}
      <ConnectWalletModal
        open={walletModalOpen}
        onClose={closeWalletModal}
        onSelectMetaMask={handleSelectMetaMask}
        onSelectCrossmint={handleSelectCrossmint}
        connecting={connecting}
        error={connectError}
      />
    </WalletsContext.Provider>
  )
}

const useWallets = () => {
  const context = useContext(WalletsContext)
  if (context === undefined) {
    throw new Error('useWallets must be used within a WalletsContext')
  }
  return context
}

export { WalletsProvider, useWallets, WalletsContext }
